import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  Alert,
  Container
} from '@mui/material';
import {
  LocalHospital,
  Facebook,
  ContactPhone,
  Email,
  Assignment,
  TrendingUp,
  Speed,
  Security,
  Group,
  Analytics,
  Phone,
  Schedule,
  DataUsage,
  Business,
  HealthAndSafety,
  AutoAwesome,
  CheckCircle,
  ArrowForward
} from '@mui/icons-material';

const About = () => {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Paper sx={{ 
          p: { xs: 4, md: 6 }, 
          mb: 4, 
          textAlign: 'center', 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <LocalHospital sx={{ fontSize: { xs: 48, md: 64 }, mb: 2, opacity: 0.9 }} />
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '2rem', md: '3rem' } }}>
              BD Dashboard
            </Typography>
            <Typography variant="h5" sx={{ mb: 3, opacity: 0.9, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              Healthcare CRM System for Facebook Lead Management
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}>
              Streamline your healthcare practice by automatically capturing and managing patient inquiries from Facebook lead ads
            </Typography>
            <Button 
              variant="contained" 
              size="large" 
              sx={{ 
                bgcolor: 'white', 
                color: 'primary.main',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': { bgcolor: 'grey.100' }
              }}
              endIcon={<ArrowForward />}
            >
              Get Started
            </Button>
          </Box>
        </Paper>

        {/* Service Overview */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <HealthAndSafety color="primary" sx={{ fontSize: 40 }} />
              Our Healthcare CRM Solution
            </Typography>
            
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
              Our comprehensive CRM system is specifically designed for healthcare businesses to efficiently manage 
              patient inquiries generated from Facebook lead advertisements. We bridge the gap between your marketing 
              efforts and patient care by automating the lead capture and management process.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 3, bgcolor: 'primary.50', borderRadius: 2, height: '100%' }}>
                  <Facebook sx={{ fontSize: 32, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom color="primary.main">
                    Facebook Integration
                  </Typography>
                  <Typography variant="body2">
                    Seamlessly connect with Facebook Lead Ads to automatically capture potential patient information
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 3, bgcolor: 'success.50', borderRadius: 2, height: '100%' }}>
                  <AutoAwesome sx={{ fontSize: 32, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom color="success.main">
                    Automated Workflow
                  </Typography>
                  <Typography variant="body2">
                    Eliminate manual data entry and ensure no potential patients slip through the cracks
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Services Provided */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Assignment color="primary" sx={{ fontSize: 40 }} />
              Services Provided
            </Typography>

            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>Complete Lead Management Solution:</strong> From Facebook lead capture to appointment scheduling, 
                we provide end-to-end patient inquiry management for healthcare providers.
              </Typography>
            </Alert>

            <List sx={{ mt: 2 }}>
              <ListItem sx={{ py: 2 }}>
                <ListItemIcon>
                  <Facebook sx={{ color: '#1877f2', fontSize: 32 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Automatic Facebook Lead Transfer"
                  secondary="We seamlessly capture lead information (name, phone, email, medical interests) from Facebook lead forms and instantly transfer them into our healthcare CRM system"
                  primaryTypographyProps={{ variant: 'h6', color: 'primary.main' }}
                  secondaryTypographyProps={{ variant: 'body1', sx: { mt: 1 } }}
                />
              </ListItem>
              
              <ListItem sx={{ py: 2 }}>
                <ListItemIcon>
                  <DataUsage sx={{ color: 'success.main', fontSize: 32 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Intelligent Data Organization"
                  secondary="All patient inquiries are automatically organized in our intuitive CRM dashboard, making it easy for medical staff to prioritize and manage leads effectively"
                  primaryTypographyProps={{ variant: 'h6', color: 'primary.main' }}
                  secondaryTypographyProps={{ variant: 'body1', sx: { mt: 1 } }}
                />
              </ListItem>

              <ListItem sx={{ py: 2 }}>
                <ListItemIcon>
                  <ContactPhone sx={{ color: 'warning.main', fontSize: 32 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Multi-Channel Follow-up Tools"
                  secondary="Enable medical staff to efficiently call, email, or schedule appointments with potential patients directly from the platform"
                  primaryTypographyProps={{ variant: 'h6', color: 'primary.main' }}
                  secondaryTypographyProps={{ variant: 'body1', sx: { mt: 1 } }}
                />
              </ListItem>

              <ListItem sx={{ py: 2 }}>
                <ListItemIcon>
                  <Analytics sx={{ color: 'info.main', fontSize: 32 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Performance Analytics & Insights"
                  secondary="Track which Facebook ads generate the most patient interest and optimize your marketing spend for maximum ROI"
                  primaryTypographyProps={{ variant: 'h6', color: 'primary.main' }}
                  secondaryTypographyProps={{ variant: 'body1', sx: { mt: 1 } }}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* How Platform Data is Used */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Security color="primary" sx={{ fontSize: 40 }} />
              How Platform Data is Used
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, height: '100%', bgcolor: 'background.paper' }}>
                  <Facebook sx={{ fontSize: 40, color: '#1877f2', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Data Collection
                  </Typography>
                  <Typography variant="body2">
                    We collect basic contact details and health interests from Facebook lead forms with full patient consent
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, height: '100%', bgcolor: 'background.paper' }}>
                  <DataUsage sx={{ fontSize: 40, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Data Organization
                  </Typography>
                  <Typography variant="body2">
                    Information is automatically organized in our secure CRM dashboard for easy access and management
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, height: '100%', bgcolor: 'background.paper' }}>
                  <ContactPhone sx={{ fontSize: 40, color: 'warning.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Patient Outreach
                  </Typography>
                  <Typography variant="body2">
                    Medical staff can call, email, or schedule appointments with these leads through our platform
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body1">
                <strong>Privacy & Security:</strong> All patient data is handled in compliance with HIPAA regulations 
                and healthcare privacy standards. We implement enterprise-grade security measures to protect sensitive information.
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* How Clients Use the Service */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Business color="primary" sx={{ fontSize: 40 }} />
              How Healthcare Providers Use Our Service
            </Typography>

            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
              Healthcare providers across various specialties use our comprehensive CRM system to:
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Centralized Lead Management"
                      secondary="View all new patient inquiries in one organized, easy-to-navigate dashboard"
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Smart Lead Assignment"
                      secondary="Automatically assign leads to appropriate medical staff based on specialization and availability"
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Follow-up Tracking"
                      secondary="Monitor and track all follow-up calls, emails, and appointment scheduling activities"
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Marketing ROI Analysis"
                      secondary="Identify which Facebook ads generate the most qualified patient leads and optimize ad spend"
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Team Performance Monitoring"
                      secondary="Track team productivity, response times, and conversion rates from leads to patients"
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Automated Reporting"
                      secondary="Generate detailed reports on lead generation, conversion rates, and practice growth metrics"
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Business Benefits */}
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <TrendingUp color="primary" sx={{ fontSize: 40 }} />
              Business Benefits
            </Typography>

            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>Proven Results:</strong> Healthcare practices using our CRM system report 40% faster lead response times 
                and 25% increase in patient conversions from Facebook advertising.
              </Typography>
            </Alert>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6} lg={3}>
                <Paper sx={{ p: 3, textAlign: 'center', height: '100%', bgcolor: 'primary.50' }}>
                  <Speed sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" color="primary.main" gutterBottom>
                    Eliminate Manual Entry
                  </Typography>
                  <Typography variant="body2">
                    Save hours of administrative work by automating the lead capture and data entry process
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Paper sx={{ p: 3, textAlign: 'center', height: '100%', bgcolor: 'success.50' }}>
                  <Security sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" color="success.main" gutterBottom>
                    Zero Missed Leads
                  </Typography>
                  <Typography variant="body2">
                    Ensure no potential patients are overlooked with automated lead capture and organized follow-up
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Paper sx={{ p: 3, textAlign: 'center', height: '100%', bgcolor: 'warning.50' }}>
                  <Group sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                  <Typography variant="h6" color="warning.main" gutterBottom>
                    Grow Patient Base
                  </Typography>
                  <Typography variant="body2">
                    Efficiently convert more Facebook leads into actual patients and grow your practice
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <Paper sx={{ p: 3, textAlign: 'center', height: '100%', bgcolor: 'info.50' }}>
                  <Analytics sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
                  <Typography variant="h6" color="info.main" gutterBottom>
                    Data-Driven Decisions
                  </Typography>
                  <Typography variant="body2">
                    Make informed marketing decisions with detailed analytics and performance insights
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom color="primary.main">
                Transform Your Healthcare Practice Today
              </Typography>
              <Typography variant="body1" paragraph>
                Join hundreds of healthcare providers who have streamlined their patient acquisition process 
                and grown their practice more efficiently with BD Dashboard.
              </Typography>
              <Chip 
                label="HIPAA Compliant" 
                color="success" 
                sx={{ mr: 1, mb: 1 }}
              />
              <Chip 
                label="Enterprise Security" 
                color="primary" 
                sx={{ mr: 1, mb: 1 }}
              />
              <Chip 
                label="24/7 Support" 
                color="info" 
                sx={{ mr: 1, mb: 1 }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Contact Section */}
        <Card>
          <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
              <Email color="primary" sx={{ fontSize: 40 }} />
              Get Started Today
            </Typography>

            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
              Ready to transform your healthcare practice with automated lead management? 
              Contact us to learn more about our comprehensive CRM solution.
            </Typography>

            <Box sx={{ 
              bgcolor: 'primary.50', 
              p: 3, 
              borderRadius: 2, 
              mb: 3,
              maxWidth: 400,
              mx: 'auto'
            }}>
              <Typography variant="h6" color="primary.main" gutterBottom>
                Contact Information
              </Typography>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                <Email color="primary" />
                <strong>bddashboardcrm@gmail.com</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Response within 24 hours
              </Typography>
            </Box>

            <Button 
              variant="contained" 
              size="large" 
              sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
              endIcon={<ArrowForward />}
            >
              Schedule a Demo
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default About;